import React, {useContext, useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router';
import {Box, Button, Paper, Typography} from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import '../login/style.css';
import {AppContext} from '../../context/app-context';
import {accountRegistrationEndpoints} from '../../requests/api-endpoints';
import {VerifyToken} from '../../util/TokenVerification';
import {publishVerification} from '../../services/session/verification-notifications';
import {getLandingRedirectPathForUser} from '../../routing/route-utils';

const EmailVerification = () => {
  const {refreshVerification, logout, loadingUserDetails, user} = useContext(AppContext);
  const navigate = useNavigate();
  const [token] = useState(() => window.takeEmailVerificationToken?.() || '');
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [lookupAttempt, setLookupAttempt] = useState(0);
  const [lookup, setLookup] = useState({status: 'loading'});
  const verification = useRef(null);

  useEffect(() => {
    let active = true;
    setResult(null);
    // Keep one request through effect replays; a manual retry gets a new attempt.
    if (verification.current?.attempt !== attempt) {
      verification.current = {attempt, promise: new Promise(resolve => {
        void VerifyToken(token, accountRegistrationEndpoints.verifyEmail, response => {
          if (response.status === 'VALID') publishVerification();
          resolve(response);
        });
      })};
    }
    verification.current.promise.then(response => { if (active) setResult(response); });
    return () => { active = false; };
  }, [token, attempt]);

  useEffect(() => {
    if (result?.status !== 'VALID' || loadingUserDetails) return;
    let active = true;
    setLookup({status: 'loading'});
    refreshVerification().then(account => {
      if (active) setLookup({status: 'ready', account});
    }).catch(() => {
      if (active) setLookup({status: 'error'});
    });
    return () => { active = false; };
  }, [result, loadingUserDetails, refreshVerification, lookupAttempt, user?.id, user?.loggedInUsername]);

  const confirmed = result?.status === 'VALID';
  const account = lookup.account;
  const sameAccount = account?.id === result?.accountId;
  const lookupCurrent = !account || (account.id === user?.id && account.loggedInUsername === user?.loggedInUsername);

  return (
    <Box component="section" aria-labelledby="email-verification-title" sx={{pb: {xs: 5, sm: 8}}}>
      <div className="ss-auth-background" aria-hidden="true" />
      <Box sx={{position: 'relative', mt: '-24rem', px: {xs: 2, sm: 3}, display: 'flex', justifyContent: 'center'}}>
        <Paper elevation={2} sx={{width: '100%', maxWidth: 600, p: {xs: 3, sm: 6}, borderRadius: '0.57rem'}}>
          {confirmed && <CheckCircleOutline color="success" sx={{fontSize: 48, mb: 2}} />}
          <Typography id="email-verification-title" component="h1" variant="h5" gutterBottom>
            {confirmed ? 'Email verified' : result ? 'Email verification' : 'Verifying your email'}
          </Typography>
          <Box role="status" aria-live="polite" sx={{'& .MuiButton-root': {mt: 3, width: '100%', minHeight: 44}}}>
            <Typography sx={{mb: 2}}>{result?.message || 'Please wait while we verify your link.'}</Typography>
            {confirmed && (lookup.status === 'loading' || loadingUserDetails) && <Typography>Checking your signed-in account…</Typography>}
            {confirmed && (lookup.status === 'error' || !lookupCurrent) && <>
              <Typography>Your email is verified, but we could not confirm your current session.</Typography>
              <Button onClick={() => setLookupAttempt(value => value + 1)}>Retry account check</Button>
            </>}
            {confirmed && lookup.status === 'ready' && lookupCurrent && !loadingUserDetails && (
              !account ? <>
                <Typography color="text.secondary">Sign in to access your account.</Typography>
                <Button component={Link} to="/login" variant="contained">Sign in</Button>
              </> : sameAccount && account.emailVerified ? <>
                <Typography color="text.secondary">You're already signed in. No additional sign-in is needed.</Typography>
                <Button variant="contained" onClick={() => navigate(getLandingRedirectPathForUser(account))}>Continue to account</Button>
              </> : sameAccount ? <>
                <Typography>Your account status has not updated yet.</Typography>
                <Button onClick={() => setLookupAttempt(value => value + 1)}>Retry account check</Button>
              </> : <>
                <Typography sx={{mb: 2}}>Verification does not change the account you are currently signed in to.</Typography>
                <Button variant="contained" onClick={logout}>Sign in to another account</Button>
              </>
            )}
            {result?.status === 'ERROR' && <Button variant="contained" onClick={() => setAttempt(value => value + 1)}>Retry verification</Button>}
            {(result?.status === 'INVALID' || result?.status === 'EXPIRED') &&
              <Button component={Link} to="/login">Sign in</Button>}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default EmailVerification;
